import React, { useEffect, useState } from "react";
import {
    ShoppingCart,
    CreditCard,
    Users,
    Wallet,
    TrendingUp,
    Cpu,
    Boxes,
    Clock,
    Store,
} from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { MetricCard } from "@/components/bio/MetricCard";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatUSD, formatDateTime } from "@/lib/format";

function Card({ title, icon: Icon, children, testid }) {
    return (
        <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-4">
                {Icon && <Icon className="h-4 w-4 text-bio-primary" />}
                <h3 className="text-sm font-bold text-bio-text">{title}</h3>
            </div>
            {children}
        </div>
    );
}

export default function DashboardView() {
    const [filters, setFilters] = useState({ schools: [], campuses: [], cafeterias: [] });
    const [school, setSchool] = useState("all");
    const [cafeteria, setCafeteria] = useState("all");
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dash/filters").then(({ data }) => { setFilters(data); if (data.schools.length === 1) setSchool(data.schools[0].id); });
    }, []);

    useEffect(() => {
        const params = {};
        if (school !== "all") params.school_id = school;
        if (cafeteria !== "all") params.cafeteria_id = cafeteria;
        setData(null);
        api.get("/dash/summary", { params }).then(({ data }) => setData(data));
    }, [school, cafeteria]);

    const cafsForSchool = filters.cafeterias.filter((c) => school === "all" || c.school_id === school);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard ejecutivo</h1>
                    <p className="text-bio-text-secondary mt-1 text-sm">Resumen en tiempo real (datos demo).</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <Select value={school} onValueChange={(v) => { setSchool(v); setCafeteria("all"); }}>
                        <SelectTrigger data-testid="filter-school" className="h-11 rounded-bio bg-white border-[1.5px] flex-1 sm:flex-none sm:min-w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {filters.schools.length > 1 && <SelectItem value="all">Todos los colegios</SelectItem>}
                            {filters.schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={cafeteria} onValueChange={setCafeteria}>
                        <SelectTrigger data-testid="filter-cafeteria" className="h-11 rounded-bio bg-white border-[1.5px] flex-1 sm:flex-none sm:min-w-[150px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas las cafeterías</SelectItem>
                            {cafsForSchool.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {!data ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-bio-card" />)}</div>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
                        <MetricCard label="Ventas de hoy" value={formatUSD(data.kpis.sales_today)} icon={ShoppingCart} data-testid="kpi-sales-today" />
                        <MetricCard label="Transacciones" value={data.kpis.transactions} icon={CreditCard} data-testid="kpi-transactions" />
                        <MetricCard label="Estudiantes activos" value={data.kpis.active_students} icon={Users} data-testid="kpi-active-students" />
                        <MetricCard label="Saldo total wallets" value={formatUSD(data.kpis.wallet_total)} icon={Wallet} data-testid="kpi-wallet-total" />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6">
                        <Card title="Productos más vendidos" icon={TrendingUp} testid="block-top-products">
                            {data.top_products.length ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={data.top_products} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7A99" }} interval={0} angle={-15} textAnchor="end" height={50} />
                                        <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                                            {data.top_products.map((_, i) => <Cell key={i} fill="#00508A" />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyState title="Sin ventas" />}
                        </Card>

                        <Card title="Estado de cafeterías" icon={Store} testid="block-cafeterias">
                            <div className="space-y-2">
                                {data.cafeterias.map((c) => (
                                    <div key={c.id} className="flex items-center justify-between py-2 border-b border-bio-border-subtle last:border-0">
                                        <span className="text-sm font-medium">{c.name}</span>
                                        <StatusBadge status={c.status} />
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card title="Estado de dispensadoras" icon={Cpu} testid="block-dispensers">
                            <div className="space-y-3">
                                {data.dispensers.map((d) => (
                                    <div key={d.id}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium truncate">{d.name}</span>
                                            <StatusBadge status={d.status} />
                                        </div>
                                        <div className="h-2 rounded-full bg-bio-bg-alt overflow-hidden">
                                            <div className={`h-full rounded-full ${d.level < 15 ? "bg-bio-error" : d.level < 40 ? "bg-bio-warning" : "bg-bio-success"}`} style={{ width: `${d.level}%` }} />
                                        </div>
                                        <p className="text-xs text-bio-text-muted mt-1">{d.level}% de nivel</p>
                                    </div>
                                ))}
                                {!data.dispensers.length && <EmptyState title="Sin dispensadoras" />}
                            </div>
                        </Card>

                        <Card title="Inventario crítico" icon={Boxes} testid="block-critical">
                            {data.critical_inventory.length ? (
                                <div className="space-y-2">
                                    {data.critical_inventory.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between py-1.5">
                                            <span className="text-sm font-medium truncate">{p.name}</span>
                                            <span className="text-xs font-bold text-bio-error">{p.stock} uds</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <EmptyState title="Inventario en orden" />}
                        </Card>

                        <Card title="Transacciones pendientes" icon={Clock} testid="block-pending">
                            {data.pending_transactions.length ? (
                                <div className="space-y-2">
                                    {data.pending_transactions.map((t) => (
                                        <div key={t.id} className="flex items-center justify-between py-1.5">
                                            <span className="text-sm truncate">{t.description}</span>
                                            <span className="flex items-center gap-2"><span className="text-sm font-semibold">{formatUSD(t.amount)}</span><StatusBadge status={t.status} /></span>
                                        </div>
                                    ))}
                                </div>
                            ) : <EmptyState title="Sin pendientes" />}
                        </Card>

                        <Card title="Actividad reciente" icon={Clock} testid="block-activity">
                            <div className="space-y-2">
                                {data.recent_activity.map((t) => (
                                    <div key={t.id} className="flex items-center justify-between py-1.5">
                                        <div className="min-w-0">
                                            <p className="text-sm truncate">{t.description}</p>
                                            <p className="text-xs text-bio-text-muted">{formatDateTime(t.created_at)}</p>
                                        </div>
                                        <StatusBadge status={t.status} />
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}
